import { isEmpty } from 'lodash';
import { BOXLESS, PICKUP } from '../../constants/returnMethods';
import { getUrlInfo } from '../../lib/url';
import { store } from '../../store';
import { returnMethodSidebar } from '../return-method-sidebar-service';
import { ALL_DAY_TIME_WINDOW } from '../../constants/pickups';
import { orderService } from './order-service';
import { returnFeeService } from '../return-fee/return-fee';
import { retailerSettingsService } from '../../services';

export const postDataOptions = () => {
  const {
    options: { selectedOptions },
  } = store.getState();
  return Object.keys(selectedOptions).reduce((valuesByName, optionName) => {
    const option = selectedOptions[optionName];
    if (option.post_data) valuesByName[optionName] = option.value;
    return valuesByName;
  }, {});
};

export const additionalProcessParamsInfo = () => {
  const {
    page: { productType },
    returnsSettings: { [productType]: { additionalProcessParams = [] } = {} } = {},
  } = store.getState();

  const { params: urlParams } = getUrlInfo();
  return additionalProcessParams.reduce((accumulator, current) => {
    if (urlParams[current]) {
      return { ...accumulator, [current]: urlParams[current] };
    }
    return accumulator;
  }, {});
};

export const repairsExpressUpgradeInfo = () => {
  const { options: { selectedOptions } = {}, repairs: { expressUpgradeFee } = {} } =
    store.getState();
  const expressUpgradeSelected = selectedOptions.express_upgrade;
  const expressUpgradeInfo = expressUpgradeSelected && expressUpgradeFee;
  return expressUpgradeInfo ? { express_upgrade_fee: expressUpgradeFee } : {};
};

export const shippingAddressInfo = () => {
  const {
    addresses: { shippingAddress } = {},
    page: { productType, locale, giftOrder = false } = {},
    returnOptions: { selectedOptionId } = {},
    returnsSettings: {
      [productType]: {
        sidebarShippingMethodConfig,
        returnOptions: {
          enabled: returnOptionsEnabled = false,
          methods: returnOptionsMethods = {},
        } = {},
        disableGiftAddressForm = false,
      } = {},
    } = {},
  } = store.getState();

  const hasSidebarAddressConfig =
    sidebarShippingMethodConfig && sidebarShippingMethodConfig.length > 0;
  const hasFormAddressConfig =
    returnOptionsEnabled &&
    Object.values(returnOptionsMethods).some(
      ({ get_address: getAddress }) => getAddress === true && selectedOptionId !== PICKUP,
    );
  const useGiftAddressSidebar = giftOrder && locale === 'en_US' && !returnOptionsEnabled;
  const collectGiftAddress = giftOrder && locale === 'en_US' && !disableGiftAddressForm;

  const getShippingAddress =
    shippingAddress &&
    (hasSidebarAddressConfig ||
      hasFormAddressConfig ||
      useGiftAddressSidebar ||
      collectGiftAddress);
  let mappedAddress = {};

  if (getShippingAddress) {
    const storedAddress = returnMethodSidebar.getMappedAddress(shippingAddress);
    mappedAddress = Object.values(storedAddress).every((value) => value === null)
      ? {}
      : storedAddress;
  }

  return mappedAddress;
};

export const getDropOffLocation = () => {
  const {
    storeLocator: { selectedLocation, country: storeLocatorCountryCode } = {},
    returnOptions: { selectedOptionMethodKey, returnOptionMethods } = {},
  } = store.getState();
  const selectedReturnOptionMethod = returnOptionMethods[selectedOptionMethodKey];

  if (isEmpty(selectedLocation) || isEmpty(selectedLocation.store_address)) return undefined;
  const carrier =
    selectedReturnOptionMethod.carrier === '' ? null : selectedReturnOptionMethod.carrier;

  /* eslint-disable camelcase */
  const {
    location_id,
    location_directions,
    location_type,
    brand_display_name: brandDisplayname,
    brand: locationName,
    store_name: display_label,
    phone_number: phone,
    latitude: lat,
    longitude: lng,
    store_address: {
      address_line1: street_1,
      address_line2: street_2,
      region_code: state,
      city,
      postal_code: zip,
      country,
    },
    additional_details,
    logos: { square_2x: { url: logo } = {} } = {},
  } = selectedLocation;
  /* eslint-enable camelcase */

  return {
    location_id,
    location_directions,
    location_type,
    location_name: brandDisplayname || locationName,
    display_label,
    type: carrier,
    phone: phone || null,
    lat,
    lng,
    street_1,
    street_2,
    state,
    city,
    zip,
    zip_2: null,
    country,
    country_code: storeLocatorCountryCode,
    additional_details,
    logo,
  };
};

export const refundInfo = () => {
  const {
    page: { productType },
    returnsSettings: {
      [productType]: { refunds: { enable_api: refundApiEnabled = false } = {} } = {},
    },
    refunds: { selectedType: selectedApiRefundOptionKey, estimate: { methods = [] } = {} } = {},
  } = store.getState();

  if (!refundApiEnabled) return {};

  const selectedObject = methods.find((option) => option.type === selectedApiRefundOptionKey) || {};
  const { reference_id: selectedApiRefundOptionId = '' } = selectedObject;

  if (!selectedApiRefundOptionKey) return {};
  return {
    refund_option: selectedApiRefundOptionKey,
    refund_estimates_id: selectedApiRefundOptionId,
  };
};

export const getLandingFormPostValues = () => {
  const {
    page: { productType },
    landingPage: { landingForm },
    returnsSettings: {
      [productType]: { validation: { input_controls: inputControls = [] } = {} } = {},
    },
  } = store.getState();

  return inputControls.reduce((constructedPostBody, row) => {
    row.forEach((input) => {
      const { name, returnSubmitType } = input;
      if (returnSubmitType === 'instore') {
        // No affiliation with the store return method - this was added for data collection from in-store associates.
        // Example: Store ID and Associate ID.
        constructedPostBody.store = {
          ...constructedPostBody.store,
          [name]: landingForm[name],
        };
      }
    });

    return constructedPostBody;
  }, {});
};

export const getSubmittedPickup = () => {
  const { returnMethods: { selectedMethod } = {}, carrierPickup: { date: pickupDate } = {} } =
    store.getState();

  return {
    pickup_date: selectedMethod.id === PICKUP ? pickupDate : undefined,
  };
};

export const getPickupData = () => {
  const { pickup: { form: pickupForm } = {} } = store.getState();
  const pickupScheduled = pickupForm && pickupForm.date;
  return pickupScheduled
    ? {
        pickup_date: pickupForm.date,
        pickup_time: pickupForm.pickupTime || ALL_DAY_TIME_WINDOW,
        pickup_instructions: pickupForm.instructions,
        pickup_phone_number: pickupForm.phoneNumber || undefined,
      }
    : {};
};

export const getCarrierPickupData = () => {
  const {
    returnOptions: { selectedOptionId } = {},
    carrierPickup: {
      date: carrierPickupDate,
      timeWindow: carrierPickupTimeWindow,
      address: carrierPickupAddress,
      timeZone: carrierPickupTimeZone,
    } = {},
  } = store.getState();

  return selectedOptionId === PICKUP
    ? {
        pickup_date: carrierPickupDate,
        pickup_time: carrierPickupTimeWindow,
        ...(carrierPickupTimeZone ? { time_zone: carrierPickupTimeZone } : {}),
        ...carrierPickupAddress,
      }
    : {};
};

export const getAttestationData = () => {
  const {
    page: { productType },
    returnsSettings: { [productType]: { attestation } = {} },
    returnSubmission: { attestation: attestationData } = {},
  } = store.getState();

  return attestation && attestation.enabled
    ? {
        attestation: attestationData,
      }
    : {};
};

export const getUnevenExchangeData = () => {
  const { unevenExchanges: { createdCart, paymentAuthorization, isUnevenExchangeSubmit } = {} } =
    store.getState();
  if (!isUnevenExchangeSubmit) return {};
  return {
    exchange_info: {
      exchange_order_id: createdCart?.order_id,
    },
    // TODO: Remove placeholder
    payment_reference: paymentAuthorization || 'placeholder',
  };
};

export const getEnhancedSimpleReturnsData = () => {
  const { 
    page: {
      countryCode,
      initialPageLoadParams: { customer_id: customerId = null } = {},
    } = {},
    landingPage: { landingForm = {} } = {},
  } = store.getState();
  if (!retailerSettingsService.getIsSimpleReturnsEnhanced()) return {};
  return {
    simple_return: true,
    country: countryCode,
    ...landingForm,
    customer_id: orderService.getCustomerId() || customerId,
  };
};

export const extendOrder = () => {
  const estimatedRefund = returnFeeService.getEstimatedRefund();
  const {
    page: { giftOrder = false, giftEmail } = {},
    refunds: {
      selectedType,
      itemizationsByType: {
        [selectedType]: { estimated_refund: { value: estimatesApiRefundAmount } = {} } = {},
      } = {},
    } = {},
    returnMethods: { selectedMethodKey } = {},
    payments: { paymentAuthorization } = {},
    stripe: { service: stripeService, narvarPaymentId } = {},
  } = store.getState();

  const { token: { id: paymentReference } = {} } = paymentAuthorization || {};
  orderService.extendConfirmOrder({
    ...postDataOptions(),
    ...additionalProcessParamsInfo(),
    ...repairsExpressUpgradeInfo(),
    ...shippingAddressInfo(),
    customer_id: orderService.getCustomerId(),
    gift_email_address: giftEmail,
    gift: giftOrder,
    drop_off_location: getDropOffLocation(),
    estimated_refund: estimatesApiRefundAmount || estimatedRefund,
    ...refundInfo(),
    payment_reference: paymentReference,
    narvar_payment_id:
      stripeService === selectedMethodKey || selectedMethodKey === BOXLESS
        ? narvarPaymentId
        : undefined,
    ...getLandingFormPostValues(),
    ...getSubmittedPickup(),
    ...getPickupData(),
    ...getCarrierPickupData(),
    ...getAttestationData(),
    ...getUnevenExchangeData(),
    ...getEnhancedSimpleReturnsData(),
  });
};
